import { Card } from '@/components/ui/card';
import { SpriteIcon } from '@/components/ui/SpriteIcon';
import { FEATURE_ICONS, FeatureIconVariant } from '@/lib/sprite-config';
import { m } from 'motion/react';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import {
  fadeInUp,
  staggerContainer,
  staggerChild,
  defaultViewport,
  cardHover,
  getReducedMotionVariants,
} from '@/lib/motion';
import { useLandingSectionTracking } from '@/hooks/useLandingSectionTracking';
import { useTranslation } from '@/i18n/i18n';

export const Features = () => {
  const shouldReduceMotion = useReducedMotion();
  const sectionRef = useLandingSectionTracking('features');
  const { t } = useTranslation();

  const features: {
    title: string;
    subtitle: string;
    description: string;
    icon: FeatureIconVariant;
    featured?: boolean;
  }[] = [
    {
      title: t('features.card1.title'),
      subtitle: t('features.card1.subtitle'),
      description: t('features.card1.description'),
      icon: 'taxFiling',
    },
    {
      title: t('features.card2.title'),
      subtitle: t('features.card2.subtitle'),
      description: t('features.card2.description'),
      icon: 'paychecks',
      featured: true,
    },
    {
      title: t('features.card3.title'),
      subtitle: t('features.card3.subtitle'),
      description: t('features.card3.description'),
      icon: 'cloud',
    },
  ];

  return (
    <section
      ref={sectionRef}
      id="features"
      aria-labelledby="features-heading"
      className="py-16 px-6 md:px-8 bg-background"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <m.div
          className="text-center mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={defaultViewport}
          variants={shouldReduceMotion ? getReducedMotionVariants(fadeInUp) : fadeInUp}
        >
          <h2
            id="features-heading"
            className="text-3xl md:text-4xl lg:text-5xl font-semibold mb-6 tracking-tight"
          >
            {t('features.heading1')}
            <br />
            <span className="text-primary">{t('features.heading2')}</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('features.subheading')}
          </p>
        </m.div>

        {/* Features grid */}
        <m.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8"
          initial="hidden"
          whileInView="visible"
          viewport={defaultViewport}
          variants={
            shouldReduceMotion ? getReducedMotionVariants(staggerContainer) : staggerContainer
          }
        >
          {features.map((feature) => (
            <m.div
              key={feature.icon}
              variants={shouldReduceMotion ? getReducedMotionVariants(staggerChild) : staggerChild}
              whileHover={shouldReduceMotion ? undefined : cardHover}
            >
              <Card
                className={`group relative p-8 h-full transition-shadow duration-300 ${
                  feature.featured
                    ? 'border border-foreground/20 shadow-lg md:scale-[1.02]'
                    : 'border border-border/50 hover:shadow-lg hover:border-primary/20'
                }`}
              >
                {/* Ghost feature icon */}
                <div className="mb-6">
                  <SpriteIcon
                    src={FEATURE_ICONS.src}
                    col={FEATURE_ICONS.variants[feature.icon].col}
                    row={FEATURE_ICONS.variants[feature.icon].row}
                    cols={FEATURE_ICONS.cols}
                    rows={FEATURE_ICONS.rows}
                    size={64}
                    className="rounded-xl"
                  />
                </div>

                <h3 className="text-2xl font-semibold text-foreground mb-2">{feature.title}</h3>
                <p className="text-base text-muted-foreground font-semibold mb-4">
                  {feature.subtitle}
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>

                {feature.featured && (
                  <div className="mt-6 pt-4 border-t border-border">
                    <span className="font-mono text-xs font-bold text-muted-foreground uppercase tracking-widest">
                      {t('features.biggestBenefit')}
                    </span>
                  </div>
                )}
              </Card>
            </m.div>
          ))}
        </m.div>
      </div>
    </section>
  );
};
